import { useEffect, useRef } from 'react';
import { Bold, Italic, Link2, List, ListOrdered, RemoveFormatting, Underline } from 'lucide-react';
import { useDialog } from '../../context/DialogContext';

export interface RichTextEditorProps {
  /** Current HTML value (controlled from outside on reset/open). */
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

const TOOLBAR_BUTTON_CLASS =
  'p-1.5 text-gray-500 rounded hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40';

/**
 * Lightweight WYSIWYG editor (bold/italic/underline, lists, links) producing
 * email-safe HTML — a contentEditable surface, no external editor dependency.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  ariaLabel = 'Rich text editor',
  disabled,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  // Note: `alert` here intentionally shadows globalThis.alert inside the component.
  const { prompt, alert } = useDialog();

  // Sync external value only while the editor is not focused (avoids caret jumps).
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && editor.innerHTML !== value && document.activeElement !== editor) {
      editor.innerHTML = value;
    }
  }, [value]);

  function emitChange() {
    onChange(editorRef.current?.innerHTML ?? '');
  }

  function exec(command: string, arg?: string) {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitChange();
  }

  async function handleLink() {
    // The dialog steals focus and destroys the contentEditable selection —
    // save the range before opening it, restore before executing the command.
    const selection = window.getSelection();
    const savedRange =
      selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;
    const input = await prompt({
      title: 'Insert Link',
      message: 'Link URL (https://…)',
      placeholder: 'https://example.com',
      confirmLabel: 'Insert',
    });
    if (!input) return;
    const trimmed = input.trim();
    // Bare domains get https://; anything not http(s) (javascript:, data:…) is rejected.
    const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
    if (!/^https?:\/\//i.test(candidate)) {
      await alert({
        title: 'Invalid URL',
        message: 'Please enter a URL starting with https:// or http://',
        variant: 'error',
      });
      return;
    }
    // Restore the selection before exec
    editorRef.current?.focus();
    if (savedRange) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(savedRange);
    }
    exec('createLink', candidate);
  }

  const buttons: { icon: typeof Bold; label: string; action: () => void }[] = [
    { icon: Bold, label: 'Bold', action: () => exec('bold') },
    { icon: Italic, label: 'Italic', action: () => exec('italic') },
    { icon: Underline, label: 'Underline', action: () => exec('underline') },
    { icon: List, label: 'Bullet list', action: () => exec('insertUnorderedList') },
    { icon: ListOrdered, label: 'Numbered list', action: () => exec('insertOrderedList') },
    { icon: Link2, label: 'Insert link', action: () => void handleLink() },
    { icon: RemoveFormatting, label: 'Clear formatting', action: () => exec('removeFormat') },
  ];

  return (
    <div className="border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-primary-500 bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100">
        {buttons.map(({ icon: Icon, label, action }) => (
          <button
            key={label}
            type="button"
            title={label}
            aria-label={label}
            disabled={disabled}
            // preventDefault keeps the editor selection alive through the click
            onMouseDown={(e) => e.preventDefault()}
            onClick={action}
            className={TOOLBAR_BUTTON_CLASS}
          >
            <Icon size={15} />
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        contentEditable={!disabled}
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        className="min-h-[160px] max-h-[380px] overflow-y-auto px-3 py-2 text-sm text-gray-900 focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
        data-testid="rich-text-editor"
      />
    </div>
  );
}
